import jsPDF from "jspdf";

// Colors match /app/frontend/src/lib/exportPdf.js so both PDFs feel like a single kit.
const INK = [26, 26, 24];
const MUTED = [92, 90, 85];
const BORDER = [215, 210, 203];
const CREAM = [252, 250, 248];
const ACCENT = [166, 93, 71];

const setColor = (doc, rgb) => { doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.setDrawColor(rgb[0], rgb[1], rgb[2]); };
const setFill = (doc, rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);

const RULES = [
  "Nel rispetto di un'equilibrata ripartizione delle varie categorie indicate, scegliere la porzione di alimento e l'eventuale abbinamento più adatti a colazione, pranzo, cena e pasti intermedi.",
  "Nel caso in cui si impieghino multipli di \"porzione\", si potranno raddoppiare (o più) soltanto gli alimenti di prima scelta (ossia quelli sprovvisti di contrassegno 2).",
  "L'utilizzo di verdure crude, cotte e minestroni di verdure, salvo intolleranze, rimane del tutto facoltativo e peraltro suggeribile. Tutti gli ortaggi diversi dai tuberi e dai legumi (indicati nel porzionario), saranno pertanto esonerati da quantificazione.",
  "Il consumo di tuberi e legumi, mostrando una maggiore concentrazione di glucidi (leggi carboidrati), risulterà essere meno permissivo dell'ortaggio comune, invitando dunque l'utente a quantificarne l'apporto considerando le stesse equivalenze espresse in tabella.",
  "Un occhio di riguardo al condimento da parte delle fonti lipidiche tradizionali (olio e burro), che essendo alimenti ad alto contributo energetico, saranno capaci di incidere in modo significativo sul monte calorico del vostro piano dietetico; d'altro canto, l'impiego di spezie (le più indicate sono curcuma e cannella), limone, aceto (meglio se di mele), si mostrerà libero e del tutto facoltativo.",
  "È consigliabile bere soltanto acqua, quindi nessuna bibita che apporti altre fonti nutrizionali.",
  "È opportuno rispettare il corretto fabbisogno idrico (0,3–0,4 litri per ogni 10 kg di peso corporeo al giorno, o comunque nella misura non inferiore ad un litro ogni 1000 kcal consumate), impiegando preferibilmente acqua a basso residuo fisso (< 50 mg/litro).",
  "Un occhio di riguardo circa il consumo di sostanze nervine non caloriche, come caffè e thé non deteinati; via libera invece a tisane ed infusi di qualunque tipo purché non zuccherati.",
  "Il consumo di sodio dovendo essere sempre e comunque commisurato all'effettivo apporto idrico, si attesta da un minimo di 1,5 ad un massimo di 4 gr al giorno (salvo diversamente indicato). Tenendo conto che il fabbisogno minimo sarà quasi interamente coperto dalla dieta, diamo il giusto spazio all'impiego di sale aggiunto (generalmente titolato a circa il 40% in sodio) attribuendo preferenza all'utilizzo di \"sali antichi\" come quello rosa dell'Himalaya.",
  "La cottura delle pietanze dovrebbe prevedere l'esclusivo impiego di piastra antiaderente o cestello a vapore; in alternativa possono essere utilizzati bollitura e/o pentola a pressione.",
  "Il \"cheat meal\" (sgarro), dal momento in cui potrà essere introdotto, non potrà comunque superare la frequenza massima di una volta ogni 7 giorni.",
  "Nonostante sia consentito (ed anche suggeribile) l'impiego rotativo di tutti gli alimenti soggetti a vincolo temporale (alimenti colorati), evitiamo che tale rotazione finisca per assecondare unicamente le voci con vincolo pari o superiore alle 24 h (oro, blu, rosso).",
  "Volendo quantificare un cereale (o un suo derivato) dopo cottura previa bollitura, moltiplicare il valore indicato nel porzionario per 2,5 volte.",
];

export function exportRegolamentoPdf({ studio } = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210, pageH = 297;
  const mX = 16;
  const contentW = pageW - mX * 2;

  const hasStudio = !!(studio && (studio.studio_name || studio.address || studio.phone || studio.email || studio.website));

  // ===== HEADER =====
  let cursorY = 14;
  if (hasStudio) {
    setColor(doc, INK);
    doc.setFont("times", "normal");
    doc.setFontSize(12.5);
    if (studio.studio_name) {
      doc.text(studio.studio_name, mX, cursorY);
      cursorY += 4.4;
    }
    setColor(doc, MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    if (studio.address) { doc.text(studio.address, mX, cursorY); cursorY += 3.4; }
    const contacts = [studio.phone, studio.email, studio.website].filter(Boolean).join(" · ");
    if (contacts) { doc.text(contacts, mX, cursorY); cursorY += 3.4; }
    cursorY += 1;
    setColor(doc, BORDER);
    doc.setLineWidth(0.2);
    doc.line(mX, cursorY, mX + contentW, cursorY);
    cursorY += 6;
  }

  // Accent stripe + kicker
  setFill(doc, ACCENT);
  doc.rect(mX, cursorY - 2, 20, 1.2, "F");
  setColor(doc, MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("REGOLAMENTO GENERALE", mX, cursorY + 3);

  setColor(doc, INK);
  doc.setFont("times", "normal");
  doc.setFontSize(22);
  doc.text("Regole per uno stile nutrizionale corretto", mX, cursorY + 12);

  setColor(doc, MUTED);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  const introLines = doc.splitTextToSize(
    "Osserviamo un elenco di facili regole a cui bisognerà corrispondere per approcciare in modo adeguato ad un corretto stile nutrizionale.",
    contentW,
  );
  doc.text(introLines, mX, cursorY + 19);

  let y = cursorY + 19 + introLines.length * 4 + 4;
  const bottomMargin = 16;

  // ===== RULES =====
  const numColW = 9;    // fixed left column for the number
  const textX = mX + numColW + 1;
  const textW = contentW - numColW - 1;

  RULES.forEach((rule, i) => {
    // Rule number badge
    setColor(doc, ACCENT);
    doc.setFont("times", "normal");
    doc.setFontSize(13);
    const numLabel = String(i + 1).padStart(2, "0");

    setColor(doc, INK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(rule, textW);
    const blockH = Math.max(lines.length * 4.6, 6) + 3;

    // Auto page-break
    if (y + blockH > pageH - bottomMargin) {
      doc.addPage();
      y = 20;
      // small header on continuation pages
      setColor(doc, MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text("REGOLAMENTO GENERALE — segue", mX, 12);
      setColor(doc, BORDER);
      doc.setLineWidth(0.2);
      doc.line(mX, 14, mX + contentW, 14);
    }

    // Number
    setColor(doc, ACCENT);
    doc.setFont("times", "normal");
    doc.setFontSize(13);
    doc.text(numLabel, mX, y + 3);

    // Rule text
    setColor(doc, INK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(lines, textX, y + 3);

    y += blockH;
  });

  // Footer on final page
  const footerY = pageH - 10;
  setColor(doc, MUTED);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  const brand = studio?.studio_name || "Metodo Funzionale";
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.text(`${brand} · Regolamento generale`, mX, footerY);
    const pgLabel = `${i} / ${total}`;
    doc.text(pgLabel, mX + contentW - doc.getTextWidth(pgLabel), footerY);
  }

  doc.save("Regolamento_generale.pdf");
}
